<script lang="ts">
  import { Input, Button, Label } from "flowbite-svelte";
  import Icon from "@iconify/svelte";
  import TipTapEditor from "$lib/admin/components/content/TipTapEditor/TipTapEditor.svelte";
  
  export let items: string[] = [];
  export let label: string = "";
  export let onAdd: () => void;
  export let onRemove: (index: number) => void;
  export let onEdit: ((index: number, value: string) => void) | null = null;
  export let color: "red" | "green" | "blue" | "purple" | "dark" | "light" | "none" | "primary" | "alternative" | "yellow" = "blue";
  export let icon: string = "";
  export let placeholder: string = "";
  export let editMode: boolean = true;
  export let richEditor: boolean = false;
  export let compactMode: boolean = false;

  function handleAdd(): void {
    onAdd();
  }

  function handleRemove(index: number): void {
    onRemove(index);
  }
  
  function handleChange(index: number, event: Event): void {
    const target = event.currentTarget as HTMLInputElement;
    if (!target) return;
    
    const value = target.value;
    
    if (onEdit) {
      onEdit(index, value);
    } else if (editMode) {
      items[index] = value;
    }
  }
  
  function handleRichEditorChange(index: number, value: string): void {
    if (onEdit) {
      onEdit(index, value);
    } else if (editMode) {
      items[index] = value;
    }
  }
  
  $: validatedItems = Array.isArray(items) ? items : [];
  
  $: colorClass = {
    blue: "text-blue-600 dark:text-blue-500",
    green: "text-green-600 dark:text-green-500",
    red: "text-red-600 dark:text-red-500",
    purple: "text-purple-600 dark:text-purple-500",
    yellow: "text-yellow-600 dark:text-yellow-500",
    dark: "text-gray-600 dark:text-gray-500",
    light: "text-gray-400 dark:text-gray-300",
    primary: "text-blue-600 dark:text-blue-500",
    alternative: "text-gray-500 dark:text-gray-400",
    none: "text-gray-600 dark:text-gray-500"
  }[color] || "text-blue-600 dark:text-blue-500";
</script>

<div class="mb-6">
  <div class="flex items-center mb-2">
    <h5 class="text-base font-medium {colorClass}">{label}</h5>
  </div>
  
  {#if validatedItems.length > 0}
    <div class="space-y-2 mb-4">
      {#each validatedItems as item, index (index)}
        <div class="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 p-2 rounded">
          {#if icon}
            <Icon icon={icon} class="text-green-500 h-5 w-5 flex-shrink-0" />
          {/if}
          <div class="flex-grow">
            {#if editMode}
              {#if richEditor}
                <div class="rich-editor-container">
                  <TipTapEditor 
                    text={item} 
                    placeholder={placeholder || `Enter ${label.toLowerCase()} item`}
                    on:change={({detail}) => handleRichEditorChange(index, detail.content)}
                    compactMode={compactMode}
                  />
                </div>
              {:else}
                <Input 
                  type="text" 
                  value={item}
                  on:input={(e) => handleChange(index, e)}
                  placeholder={placeholder || `Enter ${label.toLowerCase()} item`}
                  size="sm"
                />
              {/if}
            {:else}
              <span class="flex-grow">{@html item}</span>
            {/if}
          </div>
          <Button 
            size="xs"
            color="red"
            on:click={() => handleRemove(index)}
          >
            <Icon icon="heroicons:trash" class="h-4 w-4" />
          </Button>
        </div>
      {/each}
    </div>
  {:else}
    <div class="text-sm text-gray-500 italic mb-4">No {label.toLowerCase()} items added yet</div>
  {/if}
  
  <Button 
    size="xs"
    color={color}
    on:click={handleAdd}
    class="mt-2"
  >
    <Icon icon="heroicons:plus" class="h-4 w-4 mr-1" />
    Add {label} Item
  </Button>
</div>
